<template>
  <Modal :visible="visible" title="注单详情" @close="$emit('close')" maxWidth="6xl">
    <div class="space-y-6">
      <!-- 基本信息 -->
      <div class="bg-gray-50 p-4 rounded-md">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p class="text-sm font-medium text-gray-500">注单ID</p>
            <p class="mt-1 text-base font-semibold text-gray-900">{{ comparisonData?.fundsn || '-' }}</p>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-500">用户名</p>
            <p class="mt-1 text-base font-semibold text-gray-900">{{ comparisonData?.vendor_username || '-' }}</p>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-500">供应商/系统</p>
            <p class="mt-1 text-base font-semibold text-gray-900">{{ comparisonData?.game || '-' }}</p>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-500">游戏类别</p>
            <p class="mt-1 text-base font-semibold text-gray-900">{{ comparisonData?.game_category || '-' }}</p>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-500">投注时间</p>
            <p class="mt-1 text-base font-semibold text-gray-900">{{ formatUnixTime(comparisonData?.bet_at) || '-' }}</p>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-500">结算时间</p>
            <p class="mt-1 text-base font-semibold text-gray-900">{{ formatUnixTime(comparisonData?.payout_at) || '-' }}</p>
          </div>
        </div>
      </div>

      <!-- 投注详情 -->
      <div class="border border-gray-200 rounded-md overflow-hidden">
        <div class="bg-blue-50 px-4 py-2 border-b border-gray-200">
          <h3 class="text-sm font-medium text-blue-900">投注详情</h3>
        </div>
        <div class="p-4">
          <dl class="grid grid-cols-1 gap-4">
            <div class="sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-gray-500">投注金额</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ formatAmount(comparisonData?.bet_amount, comparisonData?.currency) }}
              </dd>
            </div>
            <div class="sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-gray-500">派彩金额</dt>
              <dd class="mt-1 text-sm font-medium sm:mt-0 sm:col-span-2" 
                  :class="comparisonData?.payout > 0 ? 'text-green-600' : 'text-gray-900'">
                {{ formatAmount(comparisonData?.payout, comparisonData?.currency) }}
              </dd>
            </div>
            <div class="sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-gray-500">状态</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span :class="getStatusClass(comparisonData?.status)">
                  {{ getStatusText(comparisonData?.status) }}
                </span>
              </dd>
            </div>
            <div class="sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-gray-500">币种</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ comparisonData?.currency || '-' }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- 详细玩法信息 -->
      <div v-if="comparisonData?.betInfo" class="border border-gray-200 rounded-md overflow-hidden">
        <div class="bg-green-50 px-4 py-2 border-b border-gray-200">
          <h3 class="text-sm font-medium text-green-900">玩法详情</h3>
        </div>
        <div class="p-4">
          <pre class="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-3 rounded-md">{{ JSON.stringify(comparisonData.betInfo, null, 2) }}</pre>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end items-center space-x-3">
        <button
          @click="$emit('close')"
          class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          关闭
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import Modal from '@/components/common/Modal.vue';
import { formatUnixTime } from '@/utils/date-utils.js';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  comparisonData: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close', 'fix']);

// 格式化金额
function formatAmount(amount, currency) {
  if (amount === null || amount === undefined) return '-';
  
  const numAmount = parseFloat(amount);
  const formatter = new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${formatter.format(numAmount)} ${currency || ''}`.trim();
}

// 获取状态文本
function getStatusText(status) {
  switch (parseInt(status)) {
    case 0: return '未结算';
    case 1: return '已结算';
    case 2: return '已取消';
    default: return '未知状态';
  }
}

// 获取状态样式类
function getStatusClass(status) {
  switch (parseInt(status)) {
    case 0:
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
    case 1:
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
    case 2:
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    default:
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
  }
}
</script> 